// Library Methods
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";

// API
import { updateProgressionBaseline } from "../../api";

type UpdateProgressionBaselineVariables = {
  // Must combine to exactly 2 (or both empty, to clear the baselines).
  baselineExamIds: Array<string>;
  baselineExternalExamIds: Array<string>;
};

export const useUpdateProgressionBaseline = (
  clinicId: string | undefined,
  patientId: string | undefined,
  analysisId: string | undefined
) => {
  const queryClient = useQueryClient();
  const { getToken } = useTokenAccess();
  const { t } = useTranslation();
  const { setSnackbarInfo } = useSnackbarContext();

  return useMutation({
    mutationFn: async ({
      baselineExamIds,
      baselineExternalExamIds,
    }: UpdateProgressionBaselineVariables) => {
      if (!clinicId || !patientId || !analysisId) throw new Error();

      try {
        const token = await getToken();
        return updateProgressionBaseline(
          token,
          clinicId,
          patientId,
          analysisId,
          baselineExamIds,
          baselineExternalExamIds
        );
      } catch (error) {
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["progression-analyses", analysisId],
      });

      setSnackbarInfo({
        variant: "success",
        message: t("Progression analysis updated successfully!"),
      });
    },
    onError: () => {
      setSnackbarInfo({
        variant: "error",
        message: t("Progression analysis could not be updated!"),
      });
    },
  });
};
